import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
    Box,
    Typography,
    Theme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import NoDataText from '../../components/NoDataText';
import TableContainer from '../../components/Table/TableContainer';
import { DEDUCTIONS_LIST_TABLE_LABELS } from '../../constants/tableLabels';
import usePagination from '../../hooks/usePagination';
import { ActiveSortField, DeductionData, FilterTypes } from '../../types';
import { handleSortTableData } from '../../helpers/handleSortTableData';
import { makeStyles } from 'tss-react/mui';
import { getDeductionsList } from '../../api/paymentsApi/deductionsApi';
import DeductionsTableDataRow from '../PaymentsView/DeductionsView/DeductionsTableDataRow';
import FilterCards from '../../components/FilterCards';
import { TRACK } from '../../analytics/catalog';
import theme from '../../theme';
import TableActions from '../../components/Table/TableActions';
import Loader from '../../components/Loader';

const useStyles = makeStyles()((theme: Theme) => ({
    tableLabel: {
        fontSize: '20px',
        fontWeight: 600,
        lineHeight: '120%',
        color: theme.palette.common.white,
        fontFamily: 'Poppins',
    },
    tableBox: {
        flex: 1,
        minWidth: 0,
        paddingBottom: theme.spacing(2),
    },
    accordion: {
        backgroundColor: 'transparent',
        marginTop: theme.spacing(1),
        boxShadow: 'none',
        overflow: 'hidden',
        fontFamily: 'Poppins',
        borderRadius: '8px',
    },
    accordionSummary: {
        backgroundColor: theme.palette.primary.light,
        minHeight: '56px',
        padding: '0 16px',
        fontFamily: 'Poppins',
        '& .MuiTypography-root': {
            fontWeight: 600,
            fontSize: '16px',
            color: theme.palette.common.white,
            fontFamily: 'Poppins',
        },
        flexDirection: 'row-reverse',
    },
    accordionDetails: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.primary.light,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        fontFamily: 'Poppins',
    },
    tableContainer: {
        width: '99%',
        background: 'transparent',
        border: `1px solid ${theme.palette.secondary.dark}`,
        borderRadius: '8px',
        overflow: 'auto',
    },
}));

const DeductionsTable = () => {
    const { classes } = useStyles();
    const params = useParams();
    const firmId = localStorage.getItem('firmId');

    const {
        pageNumber,
        rowsPerPage,
        setTotalCount,
        setPageNumber,
    } = usePagination();

    const [deductionsList, setDeductionsList] = useState<DeductionData[]>([]);
    const [isDataLoading, setIsDataLoading] = useState(true);

    const [activeFilter, setActiveFilter] = useState<string>();
    const [failedCount, setFailedCount] = useState(0);
    const [withholdCount, setwithholdCount] = useState(0);
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const [match, setMatch] = useState<string>();

    const [filters, setFilters] = useState<FilterTypes>({
        firmId: firmId || '',
        clientId: '',
        defendantId: '',
        vendorId: '',
        mappedDeductionStatus: '',
    });

    const [activeSortField, setActiveSortField] =
        useState<ActiveSortField | null>(null);

    const hasParams = !!Object.entries(params).length;

    const deductionFilters = [
        {
            status: 'Needs Attention',
            count: failedCount,
            color: theme.palette.error.main,
            trackId: TRACK.dashboard.deductions.needsAttention,
        },
        {
            status: 'Withheld Liens',
            count: withholdCount,
            color: '#0da8c9',
            trackId: TRACK.dashboard.deductions.withheldLiens,
        },
    ];

    const sortableFields = {
        createdAt: {
            order: '',
            fieldName: 'createdAt',
        },
        paidAt: {
            order: '',
            fieldName: 'paidAt',
        },
        updatedAt: {
            order: '',
            fieldName: 'updatedAt',
        },
    };

    const handleFilterChange = (filter?: string) => {
        setIsAccordionOpen(true);
        setIsDataLoading(true);
        setDeductionsList([]);
        setPageNumber?.(1);

        setActiveFilter(filter);

        setFilters((prev) => ({
            ...prev,
            mappedDeductionStatus:
                filter === 'Needs Attention'
                    ? 'Failed'
                    : filter === 'Withheld Liens'
                        ? 'Withhold'
                        : '',
        }));
    };

    useEffect(() => {
        setIsDataLoading(true);

        getDeductionsList({
            page: pageNumber,
            size: rowsPerPage,
            count: pageNumber === 1,
            match,
            filters: { ...filters, ...(hasParams && params) },
            activeSortField,
        })
            .then((res) => {
                if (res.data) {
                    setFailedCount(res.data.failedCount || 0);
                    setwithholdCount(res.data.withholdCount || 0);

                    if (pageNumber === 1) {
                        setTotalCount?.(res.data.totalCount || 0);
                    }

                    setDeductionsList(res.data.resultSet || []);
                }
            })
            .finally(() => {
                setIsDataLoading(false);
            });
    }, [
        pageNumber,
        rowsPerPage,
        filters,
        activeSortField,
        match,
        hasParams,
        params,
        setTotalCount,
    ]);

    return (
        <Box sx={{ marginBottom: '4px' }}>
            <Typography
                className={classes.tableLabel}
                sx={{ marginBottom: '12px' }}
            >
                Payments with Issues
            </Typography>

            <FilterCards
                filters={deductionFilters}
                activeFilter={activeFilter}
                onFilterChange={handleFilterChange}
            />

            <Accordion
                className={classes.accordion}
                sx={{ marginBottom: '14px' }}
                expanded={isAccordionOpen}
                onChange={(_, expanded) => setIsAccordionOpen(expanded)}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    className={classes.accordionSummary}
                >
                    <Typography className={classes.tableLabel}>
                        Deductions
                    </Typography>
                </AccordionSummary>

                <AccordionDetails className={classes.accordionDetails}>
                    <Box className={classes.tableBox}>
                        <TableActions
                            hasParams={hasParams}
                            match={match}
                            setMatch={(value) => {
                                // Loading is set by the fetch effect. Setting it here would leave the
                                // loader stuck when TableActions repeats setMatch('') with no refetch.
                                setMatch(value);
                                setPageNumber?.(1);
                            }}
                        />

                        {isDataLoading ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="240px"
                            >
                                <Loader colorType="warning" size={60} />
                            </Box>
                        ) : deductionsList.length === 0 ? (
                            <NoDataText marginTop="20px" />
                        ) : (
                            <TableContainer
                                headerLabels={DEDUCTIONS_LIST_TABLE_LABELS}
                                sortableFields={sortableFields}
                                activeSortField={activeSortField}
                                setActiveSortField={setActiveSortField}
                                handleSortTableData={handleSortTableData}
                                dashboard
                            >
                                <DeductionsTableDataRow
                                    tableData={deductionsList}
                                />
                            </TableContainer>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default DeductionsTable;